/**
 * @author        david.loehr@zumper.com (David Loehr)
 * @copyright     Copyright (c) 2023, Zumper
 * @description   Autocomplete bizos
 */

import { ZOOM_ADDRESS, ZOOM_CITY, ZOOM_HOOD, boxToQueryBounds } from '@zcode/nr-utils'
import type {
  AutocompleteResult as AutocompleteResultType,
  AutocompleteResults as AutocompleteResultsType,
  RawAutocompleteResult,
  RawAutocompleteResults,
} from './autocomplete.types'
import { Bizo, autoclass } from './base'

function exhaustiveCheck(param: never): never {
  throw new Error('Failed to cover all cases')
}

export const AutocompleteResult = autoclass<AutocompleteResultType, RawAutocompleteResult>(
  'AutocompleteResult',
  class AutocompleteResult extends Bizo<AutocompleteResultType, RawAutocompleteResult> {},
  {
    address: 'address',
    box: (t: AutocompleteResult) => (t._data.box ? boxToQueryBounds(t._data.box) : undefined),
    city: 'city',
    country: 'country',
    features: 'features',
    hood: (t: AutocompleteResult) => (t.type === 'hood' ? t.name : ''),
    id: 'record_id',
    imageId: 'image_id',
    isDerivedFromEntity: 'is_derived_from_entity',
    isPreviousSearch: 'is_previous_search',
    lat: 'lat',
    lng: 'lng',
    name: 'name',
    state: 'state',
    type: 'record_type',
    url: 'url',
    zipcode: 'zipcode',
    zoom: (t: AutocompleteResult) => {
      switch (t.type) {
        case 'hood':
        case 'postcode':
          return ZOOM_HOOD
        // we don't (yet) have any poi results
        //   case 'poi':
        //     return ZOOM_STREET
        case 'address':
        case 'building':
          return ZOOM_ADDRESS
        case 'city':
          return ZOOM_CITY
        case 'current_position':
          return undefined
        default:
          exhaustiveCheck(t)
      }
    },
  }
)
export type AutocompleteResult = AutocompleteResultType

export const AutocompleteResults = autoclass<AutocompleteResultsType, RawAutocompleteResults>(
  'AutocompleteResults',
  class AutocompleteResults extends Bizo<AutocompleteResultsType, RawAutocompleteResults> {},
  {
    matches: { isArray: true, name: 'matches', type: AutocompleteResult },
    status: 'status',
  }
)

export type AutocompleteResults = AutocompleteResultsType
